import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Camera, Shield, Sparkles, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { NotificationCategory } from '@/utils/notificationSeverity';

interface EmptyNotificationsProps {
  isLoading?: boolean;
  category?: NotificationCategory;
}

const EmptyNotifications: React.FC<EmptyNotificationsProps> = ({ 
  isLoading = false,
  category = 'all'
}) => {
  const navigate = useNavigate();
  const { profileData } = useAuth();

  // Determine contextual message based on profile state
  const getContextualContent = () => {
    // Category-specific empty states
    if (category !== 'all') {
      const categoryMessages: Record<NotificationCategory, { icon: React.ReactNode; title: string; message: string }> = {
        all: { icon: <CheckCircle />, title: '', message: '' },
        connections: {
          icon: <Heart className="h-8 w-8 text-luxury-pinkBright" />,
          title: 'Nenhuma notificação de conexões',
          message: 'Quando alguém curtir você ou vocês formarem um match, você verá aqui.',
        },
        messages: {
          icon: <Sparkles className="h-8 w-8 text-blue-400" />,
          title: 'Nenhuma mensagem nova',
          message: 'Suas novas mensagens aparecerão aqui.',
        },
        profile: {
          icon: <Camera className="h-8 w-8 text-purple-400" />,
          title: 'Nenhuma visualização recente',
          message: 'Quando alguém visualizar seu perfil, você será notificado.',
        },
        system: {
          icon: <Shield className="h-8 w-8 text-amber-400" />,
          title: 'Nenhum aviso do sistema',
          message: 'Avisos importantes aparecerão aqui.',
        },
      };
      
      return categoryMessages[category];
    }

    // Check if profile needs improvement
    const photoCount = profileData?.profile_photos?.length || 0;
    const hasMinimumPhotos = photoCount >= 3;
    const isVerified = profileData?.verification_level === 'full';
    const hasBio = (profileData?.bio?.length || 0) > 30;

    // Priority-based suggestions
    if (!hasMinimumPhotos) {
      return {
        icon: <Camera className="h-8 w-8 text-amber-400" />,
        title: 'Aumente suas visualizações',
        message: `Adicione mais ${3 - photoCount} foto${3 - photoCount > 1 ? 's' : ''} para aparecer para mais pessoas.`,
        action: {
          label: 'Adicionar fotos',
          path: '/main/profile',
        },
      };
    }

    if (!isVerified) {
      return {
        icon: <Shield className="h-8 w-8 text-luxury-pinkBright" />,
        title: 'Verifique seu perfil',
        message: 'Perfis verificados recebem 3x mais interações. Leva apenas 2 minutos.',
        action: {
          label: 'Verificar agora',
          path: '/main/verify',
        },
      };
    }

    if (!hasBio) {
      return {
        icon: <Sparkles className="h-8 w-8 text-purple-400" />,
        title: 'Complete sua bio',
        message: 'Uma bio interessante aumenta suas chances de match.',
        action: {
          label: 'Editar bio',
          path: '/main/profile',
        },
      };
    }

    // Default - everything is good
    return {
      icon: <CheckCircle className="h-8 w-8 text-green-400/80" />,
      title: 'Tudo em dia!',
      message: 'Explore o feed para descobrir novas conexões.',
      action: {
        label: 'Explorar feed',
        path: '/main/feed',
      },
    };
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
        <div className="h-16 w-16 rounded-full bg-white/5 flex items-center justify-center mb-4 animate-pulse">
          <div className="h-8 w-8 rounded-full bg-white/10" />
        </div>
        <h3 className="text-xl font-medium text-white mb-2">
          Carregando notificações...
        </h3>
        <p className="text-white/60 max-w-md">
          Estamos verificando suas notificações, aguarde um momento...
        </p>
      </div>
    );
  }

  const content = getContextualContent();

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      <div className="h-16 w-16 rounded-full bg-white/5 flex items-center justify-center mb-4">
        {content.icon}
      </div>
      <h3 className="text-xl font-medium text-white mb-2">
        {content.title}
      </h3>
      <p className="text-white/60 max-w-md mb-6">
        {content.message}
      </p>
      {'action' in content && content.action && (
        <Button
          onClick={() => navigate(content.action.path)}
          className="bg-gradient-to-r from-[#C4406B] to-[#9F3764] hover:opacity-90 text-white"
        >
          {content.action.label}
        </Button>
      )}
    </div>
  );
};

export default EmptyNotifications;
